import React from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import ReactMarkdown from "react-markdown";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

/**
 * Two column promotional card layout component
 * Used for side-by-side promotional content with images
 */
const TournamentTwoColumnCards = ({ cards }) => {
  if (!cards || cards.length === 0) return null;

  return (
    <CardsContainer>
      <Row>
        {cards.map((card, index) => (
          <Col key={index} lg={6} md={12}>
            <CardWrapper
              bgColor={card.bgColor}
              bgImage={useResponsiveImageUrl(card.bgImage)}
              overlayColor={card.overlayColor}
            >
              {card.title && (
                <CardTitle color={card.titleColor} align={card.titleAlign}>
                  <ReactMarkdown>{card.title}</ReactMarkdown>
                </CardTitle>
              )}
              {card.highlightText && (
                <HighlightText className={card.bulletClass}>
                  <ReactMarkdown>{card.highlightText}</ReactMarkdown>
                </HighlightText>
              )}
              {card.image && (
                <CardImage>
                  <ResponsiveImage
                    src={card.image}
                    alt={card.imageAlt || `Card ${index + 1}`}
                  />
                </CardImage>
              )}
              {card.content && (
                <CardContent className={card.bulletClass}>
                  <ReactMarkdown>{card.content}</ReactMarkdown>
                </CardContent>
              )}
            </CardWrapper>
          </Col>
        ))}
      </Row>
    </CardsContainer>
  );
};

const CardsContainer = styled.div`
  width: 100%;
  margin: 1rem 0;
  .row {
    align-items: stretch;
  }
  @media (max-width: 768px) {
    .row {
      gap: 2rem;
    }
  }
`;

const CardWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 20px;
  padding: 2.5rem 2.5rem 0 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  border: none;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 100%;

  ${(props) =>
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `}

  ${(props) =>
    props.overlayColor &&
    `
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${props.overlayColor};
      z-index: 0;
    }
  `}

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    min-height: auto;
  }
`;

const CardImage = styled.div`
  width: 100%;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
    max-height: 200px;
    object-fit: contain;
  }
`;

const HighlightText = styled.div`
  line-height: 1.4;

  h2 {
    color: #ffd700;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6);
  }

  p {
    margin: 0;
    line-height: 1.6;
    font-size: 1.1rem;
  }

  &.yellow-bullets {
    text-align: center;

    h2,
    h3,
    h4 {
      color: #ffd700;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
    }

    p {
      color: var(--white);
    }
  }

  &.red-bullets {
    h2,
    h3,
    h4 {
      color: #ff4444;
    }
  }

  @media (max-width: 768px) {
  }
`;

const CardTitle = styled.div`
  color: ${(props) => props.color || "#FFFFFF"};
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  text-align: center;

  h3,
  h4 {
    color: inherit;
    margin: 0;
    line-height: 1.5;
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
  }
`;

const CardContent = styled.div`
  line-height: 1.6;
  flex: 1;
  color: #ffffff;
  text-align: center;

  h3 {
    color: #ff4444;
    font-weight: 600;
    /* margin: 1.5rem 0 0.5rem 0; */
  }

  h4 {
    color: #ff4444;
    font-weight: 600;
    /* margin: 0 0 0.5rem 0; */
  }

  p {
    /* margin-bottom: 1rem; */
    line-height: 1.6;
    text-align: left;

    img {
      max-width: 220px;
      height: auto;
      margin: 1.25rem auto;
      display: block;
    }
  }

  strong {
    font-weight: 700;
    display: block;
    /* margin-bottom: 0.3rem; */
    /* margin-top: 1rem; */
    color: #ffffff;
  }

  em {
    display: block;
    font-style: italic;
    opacity: 0.9;
  }

  ul {
    list-style: none;
    padding-left: 0;

    li {
      padding-left: 1rem;
      position: relative;
      line-height: 1.5;

      &:before {
        content: "•";
        position: absolute;
        left: 0;
        font-weight: bold;
        font-size: 1.2rem;
      }
    }
  }

  /* Red bullets for left card */
  &.red-bullets {
    text-align: center;

    p {
      text-align: center;
      margin: 0;
    }

    strong {
      color: #ff4444;
    }

    h4 {
      padding-left: 0.75rem;
      position: relative;
      text-align: center;
      display: inline-block;

      &:before {
        content: "•";
        position: absolute;
        left: -0.5rem;
        color: #ff4444;
        font-weight: bold;
        font-size: 1.2rem;
      }
    }

    ul li:before {
      color: #ff4444;
    }
  }

  /* Yellow bullets for right card */
  &.yellow-bullets {
    text-align: center;

    p {
      text-align: center;
    }

    h2 {
      text-align: center;
      color: #ffd700;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
      font-weight: 700;
    }

    h3 {
      text-align: center;
      color: #ffd700;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
    }

    h4 {
      text-align: center;
      color: #ffd700;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
    }

    strong {
      text-align: center;
      color: #ffd700;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
      font-weight: 700;
      font-size: inherit;
    }

    ul {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.4rem;
      margin: 0;
      padding-left: 0;
    }

    ul li {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding-left: 0;
    }

    ul li:before {
      position: static;
    }

    ul li:before {
      color: #ffffff;
    }
  }

  @media (max-width: 768px) {
  }
`;

export default TournamentTwoColumnCards;
